import React, { useCallback, useEffect, useReducer } from 'react';
import type { RouteComponentProps } from 'react-router-dom';
import { useLocation, useParams } from 'react-router-dom';

import { Loading } from '@this/components/shared/ui/feedbacks/loading/loading';
import { styled } from '@this/constants/themes';
import type { AvailableRepository } from '@this/domain/available_repository';
import type User from '@this/domain/user/user';
import type Department from '@this/domain/department/department';
import type Project from '@this/domain/project/project';
import type Traveler from '@this/src/domain/traveler/traveler';
import Trip from '@this/src/domain/trip/trip';
import Trip2, { convertTripResponseToArgs } from '@this/src/domain/trip/trip2';
import Link from '@this/shared/atoms/link';
import { Fetcher } from '@this/src/util';

import DraftSearch from '../trips/draft_search/draft_search';
import TripReceiptCard from './trip_receipt_card';
import { Body, BodyWrap, Wrap } from '../trips/trips';

interface Props extends RouteComponentProps {
  user: User | null;
  serviceId: number;
  availableRepos: AvailableRepository[];
  availableOptions: string[];
}

interface State {
  loading: boolean;
  error: string | null;
  service: { name: string; tel: string };
  trip: any | null;
  trip2: Trip2 | null;
  selectedDraft: any | null;
  projects: Project[];
  departments: Department[];
  showDownloadModal: boolean;
  downloadError: string | null;
  showCancelApproveModal: boolean;
  cancelReason: string;
  cancelSubmitting: boolean;
  cancelError: string | null;
  showConfirmationModal: boolean;
  confirmingTripNotifiedUsers: (Traveler | undefined)[] | null;
  excludeOrder: boolean;
  showExicModal: boolean;
  exicError: string | null;
  showWorkflowUnappliedModal: boolean;
  referrer: string;
  apiTotalPrice: number;
  invoiceOrderItems: any[];
}

interface FetchTripsResult {
  trip: any;
  trips_data: any;
  service: { name: string; tel: string };
  departments: Department[];
  projects: Project[];
  show_fee: boolean;
  user: any;
  invoice: any;
  receipt: any;
  order_items: any[];
  total_price: number;
}

type Action =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: FetchTripsResult }
  | { type: 'FETCH_FAILURE'; payload: string }
  | { type: 'TOGGLE_DRAFT_SEARCH' }
  | { type: 'SHOW_DOWNLOAD_MODAL' }
  | { type: 'HIDE_DOWNLOAD_MODAL' }
  | { type: 'SHOW_CANCEL_APPROVE_MODAL' }
  | { type: 'HIDE_CANCEL_APPROVE_MODAL' }
  | { type: 'RESET_CANCEL_SUBMITTING' }
  | { type: 'SET_CANCEL_REASON'; payload: string }
  | { type: 'RESET_CANCEL_REASON' }
  | { type: 'CANCEL_FAILURE'; payload: string }
  | { type: 'SHOW_CONFIRMATION_MODAL' }
  | { type: 'HIDE_CONFIRMATION_MODAL' }
  | { type: 'SET_CONFIRMING_TRIP_NOTIFIED_USERS'; payload: (Traveler | undefined)[] | null }
  | { type: 'TOGGLE_EXCLUDE_ORDER' }
  | { type: 'SHOW_EXIC_MODAL'; payload: string | null }
  | { type: 'HIDE_EXIC_MODAL' }
  | { type: 'TOGGLE_SHOW_WORKFLOW_UNAPPLIED_MODAL' }
  | { type: 'SET_REFERER'; payload: string };

const initialState: State = {
  loading: false,
  error: null,
  trip: null,
  trip2: null,
  selectedDraft: null,
  service: { name: '', tel: '' },
  projects: [],
  departments: [],
  showDownloadModal: false,
  downloadError: null,
  showCancelApproveModal: false,
  cancelReason: '',
  cancelSubmitting: false,
  cancelError: null,
  showConfirmationModal: false,
  confirmingTripNotifiedUsers: null,
  excludeOrder: false,
  showExicModal: false,
  exicError: null,
  showWorkflowUnappliedModal: false,
  referrer: '/trip_receipts',
  apiTotalPrice: 0,
  invoiceOrderItems: []
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, loading: true, error: null };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        loading: false,
        trip: action.payload.trip
          ? new Trip({ ...action.payload.trip, ...action.payload.trips_data, showFee: action.payload.show_fee })
          : null,
        trip2: action.payload.trip
          ? new Trip2(convertTripResponseToArgs(action.payload.trip, action.payload.show_fee))
          : null,
        service: action.payload.service || { name: '', tel: '' },
        projects: action.payload.projects || [],
        departments: action.payload.departments || [],
        apiTotalPrice: action.payload.total_price ?? 0,
        invoiceOrderItems: action.payload.order_items || []
      };
    case 'FETCH_FAILURE':
      return { ...state, loading: false, error: action.payload };
    case 'TOGGLE_DRAFT_SEARCH':
      return {
        ...state,
        selectedDraft: state.selectedDraft ? null : state.trip
      };
    case 'SHOW_DOWNLOAD_MODAL':
      return { ...state, showDownloadModal: true, downloadError: null };
    case 'HIDE_DOWNLOAD_MODAL':
      return { ...state, showDownloadModal: false };
    case 'SHOW_CANCEL_APPROVE_MODAL':
      return { ...state, showCancelApproveModal: true, cancelError: null };
    case 'HIDE_CANCEL_APPROVE_MODAL':
      return { ...state, showCancelApproveModal: false };
    case 'RESET_CANCEL_SUBMITTING':
      return { ...state, cancelSubmitting: false, cancelError: null };
    case 'SET_CANCEL_REASON':
      return { ...state, cancelReason: action.payload };
    case 'RESET_CANCEL_REASON':
      return {
        ...state,
        showCancelApproveModal: false,
        cancelReason: '',
        cancelSubmitting: false,
        cancelError: null
      };
    case 'CANCEL_FAILURE':
      return { ...state, cancelSubmitting: false, cancelError: action.payload };
    case 'SHOW_CONFIRMATION_MODAL':
      return { ...state, showConfirmationModal: true };
    case 'HIDE_CONFIRMATION_MODAL':
      return { ...state, showConfirmationModal: false, confirmingTripNotifiedUsers: null };
    case 'SET_CONFIRMING_TRIP_NOTIFIED_USERS':
      return { ...state, confirmingTripNotifiedUsers: action.payload };
    case 'TOGGLE_EXCLUDE_ORDER':
      return { ...state, excludeOrder: !state.excludeOrder };
    case 'SHOW_EXIC_MODAL':
      return { ...state, showExicModal: true, exicError: action.payload };
    case 'HIDE_EXIC_MODAL':
      return { ...state, showExicModal: false };
    case 'TOGGLE_SHOW_WORKFLOW_UNAPPLIED_MODAL':
      return { ...state, showWorkflowUnappliedModal: !state.showWorkflowUnappliedModal };
    case 'SET_REFERER':
      return { ...state, referrer: action.payload };
    default:
      return state;
  }
};

const TripReceiptPage: React.FC<Props> = ({ user, serviceId, availableRepos, availableOptions }) => {
  const { trip_id: tripId, invoice_id: invoiceId } = useParams<{ trip_id: string; invoice_id: string }>();
  const location = useLocation<{ referrer?: string }>();
  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    loading,
    error,
    trip,
    trip2,
    selectedDraft,
    projects,
    departments,
    referrer,
    apiTotalPrice,
    invoiceOrderItems
  } = state;

  const fetchTrip = useCallback(() => {
    dispatch({ type: 'FETCH_START' });

    const url = invoiceId
      ? `/trip_receipts/${tripId}/invoices/${invoiceId}.json`
      : `/trip_receipts/${tripId}/credit_card.json`;
    Fetcher.get<FetchTripsResult>(url)
      .then(res => {
        dispatch({ type: 'FETCH_SUCCESS', payload: res });
      })
      .catch(e => {
        const error = '通信環境が不安定です。\n時間をおいてもう一度お試しください。';
        dispatch({ type: 'FETCH_FAILURE', payload: error });
      });
  }, [tripId, invoiceId]);

  useEffect(() => {
    fetchTrip();
  }, [fetchTrip]);

  useEffect(() => {
    if (trip && trip.status === 8 && trip.freee_approval_request_id) {
      dispatch({ type: 'TOGGLE_SHOW_WORKFLOW_UNAPPLIED_MODAL' });
    }
  }, [trip]);

  useEffect(() => {
    if (!location.state?.referrer) {
      return;
    }

    const tempUrl = `http://localhost:3000${location.state.referrer}`;
    const refererPath = new URL(tempUrl).pathname;

    if (refererPath === '/trips') {
      dispatch({ type: 'SET_REFERER', payload: location.state.referrer });
    }
  }, [location.state?.referrer]);

  const handleToggleDraftSearch = useCallback(() => {
    dispatch({ type: 'TOGGLE_DRAFT_SEARCH' });
  }, []);

  return (
    <Wrap>
      <BodyWrap>
        <Body>
          <StyledHeader>
            <Link to={referrer}>&lt; 一覧に戻る</Link>
          </StyledHeader>
          <StyledTripsWrap>
            {loading ? (
              <Loading />
            ) : error ? (
              <StyledError>{error}</StyledError>
            ) : selectedDraft ? (
              <DraftSearch
                trip={selectedDraft}
                onClose={handleToggleDraftSearch}
                availableRepos={availableRepos}
              />
            ) : (
              trip && (
                <StyledCardWrap key={trip.id}>
                  <TripReceiptCard
                    trip={trip}
                    trip2={trip2 || undefined}
                    user={user}
                    departments={departments}
                    projects={projects}
                    serviceId={serviceId}
                    fetchTrips={fetchTrip}
                    apiTotalPrice={apiTotalPrice}
                    invoiceOrderItems={invoiceOrderItems}
                  />
                </StyledCardWrap>
              )
            )}
          </StyledTripsWrap>
        </Body>
      </BodyWrap>
    </Wrap>
  );
};

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: solid 1px #eee;
  padding-top: 10px;
  padding-bottom: 10px;
`;

const StyledError = styled.div`
  padding: 20px;
  color: ${props => props.theme.redColor};
`;

const StyledTripsWrap = styled.div`
  padding: 20px 0;
`;

const StyledCardWrap = styled.div`
  box-shadow: 0 0 3px 1px ${props => props.theme.grayBorderColor};
  border-radius: 5px;
`;

export default TripReceiptPage;
